import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import { MY_OPT_IN_PROMPTS_QUERY_KEY, type MyOptInPrompt, type OptInResponse } from '../../services/optInPromptsService';
import { useOptInPromptOptional } from './OptInPromptContext';
import { OptInPromptForm } from './OptInPromptForm';

/**
 * Opt-in prompt opened automatically once per version (OptInPromptContext).
 * "Speichern", "Nicht teilnehmen" (stored as declined) or "Später" (just closes —
 * the choice stays available in Settings → Einwilligungen).
 */
export function OptInPromptDialog() {
  const optIn = useOptInPromptOptional();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState<OptInResponse | null>(null);
  const prompt = optIn?.prompt ?? null;

  useEffect(() => {
    setSaved(null);
  }, [prompt?.id]);

  if (optIn === null || prompt === null) return null;

  const handleSaved = (response: OptInResponse): void => {
    setSaved(response);
    queryClient.setQueryData<MyOptInPrompt[]>(MY_OPT_IN_PROMPTS_QUERY_KEY, (current) =>
      current?.map((p) => (p.id === prompt.id ? { ...p, due: false, response } : p)),
    );
  };

  return (
    <Dialog
      isOpen
      onClose={optIn.close}
      title={t(`optInPrompts.${prompt.id}.title`)}
      size="md"
      footer={saved ? <Button variant="primary" onClick={optIn.close}>{t('optInPrompts.common.close')}</Button> : undefined}
      data-testid="opt-in-prompt-dialog"
    >
      {saved ? (
        <Alert tone="success" title={t(saved.granted ? 'optInPrompts.common.thanksTitle' : 'optInPrompts.common.declinedTitle')} live="polite">
          {t(saved.granted ? 'optInPrompts.common.thanksText' : 'optInPrompts.common.declinedText')}
        </Alert>
      ) : (
        <Stack gap="4">
          <Text>{t(`optInPrompts.${prompt.id}.intro`)}</Text>
          <OptInPromptForm
            prompt={prompt}
            source="prompt"
            onSaved={handleSaved}
            declineLabel={t('optInPrompts.common.decline')}
            secondaryAction={
              <Button variant="ghost" onClick={optIn.close}>
                {t('optInPrompts.common.later')}
              </Button>
            }
          />
        </Stack>
      )}
    </Dialog>
  );
}

export default OptInPromptDialog;
